// =============================================================================
// One-time bottom banner shown to first-time iOS Safari visitors hinting at
// "Add to Home Screen" so they get the standalone PWA experience (no Safari
// chrome, push notifications work, etc.).
//
// Shown only when ALL of these are true:
//   - User-agent is iPhone / iPad / iPod
//   - Page is NOT already running in standalone mode
//   - User hasn't dismissed it before (localStorage flag)
//
// Dismiss is sticky for the lifetime of the browser profile.
// =============================================================================
import React, { useEffect, useState } from 'react';
import { X, Share } from 'lucide-react';
import { isNativeApp } from '../lib/platform';
import { useTranslation } from '../lib/i18n';

const STORAGE_KEY = 'eulea_install_hint_dismissed_v1';

const isIos = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
};

const isStandalone = () => {
  if (typeof window === 'undefined') return false;
  // iOS Safari uses navigator.standalone; modern browsers use display-mode.
  return (
    window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true
  );
};

export default function InstallHint() {
  const { t } = useTranslation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Inside the native app there is nothing to install.
    if (isNativeApp || !isIos() || isStandalone()) return;
    let dismissed = false;
    try {
      dismissed = window.localStorage.getItem(STORAGE_KEY) === '1';
    } catch {
      // localStorage might be unavailable in private mode — fail open.
    }
    if (dismissed) return;
    // Small delay so it doesn't pop in right on first paint.
    const tm = setTimeout(() => setShow(true), 1200);
    return () => clearTimeout(tm);
  }, []);

  const dismiss = () => {
    setShow(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, '1');
    } catch {
      // ignore
    }
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-0 z-[90] flex justify-center px-3 pointer-events-none"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 0.75rem)' }}
      role="dialog"
      aria-label={t('install.ariaLabel')}
    >
      <div className="pointer-events-auto w-full max-w-sm bg-gray-900 text-white rounded-2xl shadow-2xl ring-1 ring-white/10 px-4 py-3 flex items-start gap-3 animate-toast-in">
        <div className="flex-shrink-0 mt-0.5 h-9 w-9 rounded-full bg-primary-600 flex items-center justify-center">
          <Share size={18} strokeWidth={2.25} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold">{t('install.title')}</p>
          <p className="text-xs text-gray-300 mt-0.5 leading-snug">
            {t('install.tapOn')}{' '}
            <span
              className="inline-flex items-center justify-center h-4 w-4 align-text-bottom mx-0.5 rounded bg-white/15"
              aria-hidden="true"
            >
              <Share size={11} />
            </span>{' '}
            {t('install.inSafariChoose')}{' '}
            <span className="font-medium text-white">{t('install.homeScreen')}</span>
            {t('install.tail')}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          className="flex-shrink-0 p-1.5 -m-1 rounded-full text-gray-400 hover:text-white hover:bg-white/10 transition-colors"
          aria-label={t('install.dismiss')}
        >
          <X size={16} />
        </button>
      </div>
    </div>
  );
}
